import React, { useState, useCallback } from "react";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Card,
  CardContent,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Box,
  TablePagination,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  MenuItem,
} from "@mui/material";
import { UploadFile, CloudUpload, Delete } from "@mui/icons-material";
import * as XLSX from "xlsx";
import axios from "axios";
import Navbar from "../Navbar";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

const FileUploadButton = ({ onFileUpload, disabled }) => (
  <label htmlFor="upload-input">
    <input
      type="file"
      accept=".xlsx"
      onChange={onFileUpload}
      style={{ display: "none" }}
      id="upload-input"
      disabled={disabled}
    />
    <Button
      variant="contained"
      color="primary"
      component="span"
      startIcon={<CloudUpload />}
      disabled={disabled}
    >
      Upload Excel
    </Button>
  </label>
);

const ExcelTable = ({ tableData, page, rowsPerPage }) => (
  <TableContainer component={Paper} sx={{ maxHeight: 450 }}>
    <Table stickyHeader>
      <TableHead>
        <TableRow>
          {Object.keys(tableData[0] || {}).map((key) => (
            <TableCell
              key={key}
              sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
            >
              {key}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {tableData
          .slice(page * rowsPerPage, (page + 1) * rowsPerPage)
          .map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {Object.values(row).map((value, cellIndex) => (
                <TableCell key={cellIndex}>{value}</TableCell>
              ))}
            </TableRow>
          ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const BulkUpload = () => {
  const navigate = useNavigate();
  const [excelData, setExcelData] = useState({});
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [activeSheet, setActiveSheet] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadFlag, setUploadFlag] = useState(""); // "RO" or "HO"
  const [successDialog, setSuccessDialog] = useState(false);

  const [destinationType, setDestinationType] = useState("existing");
  const [destinationSheet, setDestinationSheet] = useState("");
  const [newSheetName, setNewSheetName] = useState("");

  const [existingSheets, setExistingSheets] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem("token");

    axios
      .get(`${API_CONFIG.APIURL}/call/track/sheets`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        // console.log("Sheets:", res.data);

        setExistingSheets(res.data);
      })
      .catch((err) => {
        // console.log("Fetch sheets error:", err.response?.data || err.message);
      });
  }, []);

  const handleFileUpload = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith(".xlsx")) {
      setSnackbarMessage("❌ Only Excel files (.xlsx) are allowed.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setSnackbarMessage("❌ File too large. Max size is 5MB.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    setFileName(file.name);
    setUploadedFile(file);

    const reader = new FileReader();
    reader.readAsBinaryString(file);
    reader.onload = (e) => {
      const data = e.target.result;
      const workbook = XLSX.read(data, { type: "binary" });
      const allSheets = {};
      const requiredColumns = [
        "Region",
        "Area",
        "Branch",
        "Customer Id",
        "Loan Id",
        "Customer Name",
        "Mobile Number1",
        "Mobile Number2",
        "Tele-Caller",
        "Date of Data",
      ];
      const requiredColumnSet = new Set(requiredColumns);

      let isValid = true;
      let errorSheet = "";

      workbook.SheetNames.forEach((sheetName) => {
        const sheet = workbook.Sheets[sheetName];
        // const jsonData = XLSX.utils.sheet_to_json(sheet);
        const jsonData = XLSX.utils.sheet_to_json(sheet, {
          raw: false,
          dateNF: "d/m/yyyy",
        });

        if (jsonData.length === 0) return;

        const headers = Object.keys(jsonData[0]);
        const headerSet = new Set(headers);

        const hasAllRequired = requiredColumns.every((col) =>
          headerSet.has(col),
        );
        const isExactMatch =
          headerSet.size === requiredColumnSet.size &&
          [...headerSet].every((col) => requiredColumnSet.has(col));

        if (!hasAllRequired || !isExactMatch) {
          isValid = false;
          errorSheet = sheetName;
        } else {
          allSheets[sheetName] = jsonData;
        }
      });

      if (!isValid) {
        setSnackbarMessage(
          `❌ Sheet "${errorSheet}" must contain exactly these 10 columns: ${requiredColumns.join(", ")}`,
        );
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
        setExcelData({});
        setUploadedFile(null);
        setFileName("");
        return;
      }

      setExcelData(allSheets);
      setActiveSheet(workbook.SheetNames[0]);
      setPage(0);
    };
  }, []);

  // Generates and downloads an Excel report listing every validation
  // error returned by the backend (row number, column, value, reason).
  // Used when there are too many errors to show inline in the Snackbar.
  const downloadErrorReport = (errors) => {
    const worksheet = XLSX.utils.json_to_sheet(
      errors.map((e) => ({
        "Row Number": e.row,
        "Column": e.column,
        "Value": e.value,
        "Error": e.reason,
      })),
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Errors");
    XLSX.writeFile(workbook, `Upload_Errors_${Date.now()}.xlsx`);
  };

  const { API_CONFIG } = require("../../configuration");

  const handleUpload = async () => {
    if (!fileName || !uploadedFile) {
      setSnackbarMessage("❌ No file uploaded.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }
    if (destinationType === "existing" && !destinationSheet) {
      setSnackbarMessage("❌ Please select destination sheet.");

      setSnackbarSeverity("error");
      setOpenSnackbar(true);

      return;
    }

    if (destinationType === "new" && !newSheetName.trim()) {
      setSnackbarMessage("❌ Please enter new sheet name.");

      setSnackbarSeverity("error");
      setOpenSnackbar(true);

      return;
    }

    const activeData = excelData[activeSheet] || [];
    const requiredFields = Object.keys(activeData[0] || {});
    const isValid = activeData.every((row) =>
      requiredFields.every(
        (field) => row[field] !== undefined && row[field] !== "",
      ),
    );

    const loggedInUser = JSON.parse(localStorage.getItem("user") || "{}");
    const requestedBy = loggedInUser.emp_id_second;
    const requestedByemp = loggedInUser.emp_id;

    if (!requestedBy || !requestedByemp) {
      setSnackbarMessage("❌ User not logged in. Please log in and try again.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    const formData = new FormData();
    formData.append("file", uploadedFile);
    const user = JSON.parse(localStorage.getItem("user"));
    const emp_id = user?.emp_id;
    formData.append("requested_by", user?.emp_id);
    formData.append("requested_by", requestedBy);
    formData.append("requested_byemp", requestedByemp);
    formData.append("flag", uploadFlag);
    formData.append("destination_type", destinationType);

    formData.append(
      "destination_sheet",
      destinationType === "existing" ? destinationSheet : newSheetName,
    );

    try {
      setLoading(true);

      const token = localStorage.getItem("token");
      // console.log(emp_id);

      const response = await axios.post(
        `${API_CONFIG.APIURL}/call/track/bulkupload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setSnackbarMessage("✅ Upload file Successful!");
      setSnackbarSeverity("success");
      setExcelData({});
      setFileName("");
      setUploadedFile(null);
      setUploadFlag("");
      // Wait 2 seconds, then navigate
      setTimeout(() => {
        setSuccessDialog(false);
        navigate("/DashBoard");
      }, 2000);
    } catch (error) {
      console.error("Upload error:", error);

      let errMsg = "❌ Error uploading data. Please try again.";

      try {
        const data = error?.response?.data;

        // If it's a Blob, convert to text then parse JSON
        if (data instanceof Blob) {
          const text = await data.text();

          try {
            const json = JSON.parse(text);
            errMsg = json?.message || errMsg;
            // console.log("Parsed error message from blob:", errMsg);
          } catch (parseErr) {
            console.error("Failed to parse JSON from blob:", parseErr);
          }
        } else if (typeof data === "object" && data !== null) {
          if (data?.errors?.length > 5) {
            // Too many errors — generate downloadable report instead
            downloadErrorReport(data.errors);
            errMsg = `${data.errors.length} errors found. Error report downloaded — please check the file.`;
          } else if (data?.errors?.length > 0) {
            // Few errors — show inline
            errMsg = data.errors
              .map(
                (e) =>
                  `Row ${e.row} (${e.column}): ${e.reason}${
                    e.value ? ` "${e.value}"` : ""
                  }`,
              )
              .join("\n");
          } else {
            errMsg = data?.message || errMsg;
          }
        }
      } catch (innerErr) {
        console.error("Failed to handle error response:", innerErr);
      }
      setSnackbarMessage(errMsg);
      setSnackbarSeverity("error");
    } finally {
      setOpenSnackbar(true);
      setLoading(false);
    }
  };

  const handleRemoveFile = () => {
    setExcelData({});
    setFileName("");
    setUploadedFile(null);
    setPage(0);
  };

  return (
    <>
      <Navbar />
      <h2
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: -2,
        }}
      >
        Upload Sheet
      </h2>
      <Box
        sx={{
          p: { xs: 1, sm: 2, md: 3 },
          ml: { xs: 0, md: "250px" },
          width: {
            xs: "100%",
            md: "calc(100% - 250px)",
          },
          boxSizing: "border-box",
          overflowX: "hidden",
        }}
      >
        <Card elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ mt: 3, p: 2, border: "1px solid #ddd", borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              📌 Select Destination Sheet
            </Typography>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <RadioGroup
                  value={destinationType}
                  onChange={(e) => {
                    setDestinationType(e.target.value);

                    setDestinationSheet("");
                    setNewSheetName("");
                  }}
                >
                  <FormControlLabel
                    value="existing"
                    control={<Radio />}
                    label="Existing Sheet"
                  />

                  <FormControlLabel
                    value="new"
                    control={<Radio />}
                    label="Create New Sheet"
                  />
                </RadioGroup>
              </Grid>

              <Grid item xs={12} md={8}>
                {destinationType === "existing" && (
                  <TextField
                    select
                    fullWidth
                    label="Select Sheet"
                    value={destinationSheet}
                    onChange={(e) => setDestinationSheet(e.target.value)}
                  >
                    {existingSheets.map((sheet) => (
                      <MenuItem key={sheet.id} value={sheet.sheet_name}>
                        {sheet.sheet_name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                {destinationType === "new" && (
                  <TextField
                    fullWidth
                    label="New Sheet Name"
                    value={newSheetName}
                    onChange={(e) => setNewSheetName(e.target.value)}
                  />
                )}
              </Grid>
            </Grid>
          </Box>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: "primary.main" }}
              >
                📂 Bulk Upload Excel (.xlsx)
              </Typography>
            </Box>

            <Box
              onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer.files[0];
                if (
                  file &&
                  file.name.endsWith(".xlsx")
                  // && uploadFlag
                ) {
                  handleFileUpload({ target: { files: [file] } });
                }
              }}
              onDragOver={(e) => e.preventDefault()}
              sx={{
                border: "2px dashed #90caf9",
                borderRadius: 2,
                p: 2,
                textAlign: "center",
                mb: 2,
              }}
            >
              <Typography>
                Drag & drop Excel file here or click below to upload
              </Typography>
              <FileUploadButton
                onFileUpload={handleFileUpload}
                //   disabled={!uploadFlag}
              />
            </Box>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                {fileName && (
                  <Typography color="textSecondary" variant="subtitle1">
                    📄 {fileName}
                  </Typography>
                )}
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                {fileName && (
                  <IconButton color="error" onClick={handleRemoveFile}>
                    <Delete />
                  </IconButton>
                )}
              </Grid>
            </Grid>

            <Button
              variant="contained"
              color="success"
              startIcon={
                loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <UploadFile />
                )
              }
              onClick={handleUpload}
              disabled={
                Object.keys(excelData).length === 0 ||
                loading ||
                (destinationType === "existing" && !destinationSheet) ||
                (destinationType === "new" && !newSheetName.trim())
              }
              sx={{ mt: 2, width: "30%" }}
            >
              {loading ? "Uploading..." : "Submit Data"}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              component="a"
              href="/CallFormate.xlsx"
              download
              sx={{ mt: 2, ml: 2, width: "25%" }}
            >
              Download Format
            </Button>

            {Object.keys(excelData).length > 0 && (
              <>
                <Typography
                  variant="h6"
                  sx={{ mt: 4, mb: 1, fontWeight: "bold" }}
                >
                  📊 Preview Uploading Data
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    mb: 2,
                  }}
                >
                  <Tabs
                    value={activeSheet}
                    onChange={(e, newValue) => {
                      setActiveSheet(newValue);
                      setPage(0);
                    }}
                    sx={{ flexGrow: 1, minWidth: 300 }}
                  >
                    {Object.keys(excelData).map((sheet) => (
                      <Tab key={sheet} label={sheet} value={sheet} />
                    ))}
                  </Tabs>
                  <TablePagination
                    component="div"
                    count={excelData[activeSheet]?.length || 0}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    labelRowsPerPage="Rows:"
                    sx={{ mt: { xs: 2, sm: 0 } }}
                  />
                </Box>

                <ExcelTable
                  tableData={excelData[activeSheet]}
                  page={page}
                  rowsPerPage={rowsPerPage}
                />
              </>
            )}
          </CardContent>
        </Card>

        <Snackbar
          open={openSnackbar}
          autoHideDuration={snackbarSeverity === "error" ? null : 3000}
          onClose={(event, reason) => {
            if (reason === "clickaway") return;
            setOpenSnackbar(false);
          }}
        >
          <Alert
            severity={snackbarSeverity}
            onClose={() => setOpenSnackbar(false)}
            sx={{ width: "100%", whiteSpace: "pre-line" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default BulkUpload;